import React, { useEffect, useState } from 'react';

import PasswordRulesList from './PasswordRulesList';
import { isPasswordCompliant } from '../../utils/passwordPolicy';

/**
 * The password rules for an Amplify Authenticator screen whose new-password
 * input it does not own: first login (`ForceNewPassword`) and the code step of
 * Forgot Password (`ConfirmResetPassword`).
 *
 * Amplify renders the fields itself, so the value is read off
 * `input[name="password"]` (the name both routes use). The listener is attached
 * in an effect, which runs after the whole form has committed — so this works
 * even when rendered ABOVE the fields, as the ConfirmResetPassword `Header`
 * slot is (that route has no `FormFields` slot in this Amplify version).
 *
 * The primary submit stays disabled until every rule passes. `type="submit"`
 * keeps "Resend Code" (a `type="button"` link) unaffected, and only one
 * Authenticator route is mounted at a time.
 *
 * Deliberately free of Redux and MUI: it renders inside the pre-auth Amplify
 * shell, which is mounted outside the Redux `<Provider>`.
 */
export default function LivePasswordRules() {
  const [password, setPassword] = useState('');
  const compliant = isPasswordCompliant(password);

  useEffect(() => {
    const handlePasswordChange = (e: Event) => {
      setPassword((e.target as HTMLInputElement)?.value);
    };

    const passwordInput = document.querySelector('input[name="password"]');
    passwordInput?.addEventListener('input', handlePasswordChange);

    return () => {
      passwordInput?.removeEventListener('input', handlePasswordChange);
    };
  }, []);

  useEffect(() => {
    const submitButton = document.querySelector(
      '.amplify-button--primary[type="submit"]',
    ) as HTMLButtonElement | null;
    if (submitButton) {
      submitButton.disabled = !compliant;
      submitButton.classList.toggle('disabled', submitButton.disabled);
    }
  }, [compliant]);

  return <PasswordRulesList password={password} />;
}
